import { type CSSProperties, type PointerEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';

interface DialogPosition {
  left: number;
  top: number;
}

const VIEWPORT_MARGIN = 12;
const RIGHT_PANEL_TOP = 64;

interface DraggableDialogOptions {
  initialPlacement?: 'center' | 'right';
}

function clampToBounds(left: number, top: number, panelRect: DOMRect, boundsRect: DOMRect): DialogPosition {
  const minLeft = VIEWPORT_MARGIN;
  const minTop = VIEWPORT_MARGIN;
  const maxLeft = Math.max(minLeft, boundsRect.width - panelRect.width - VIEWPORT_MARGIN);
  const maxTop = Math.max(minTop, boundsRect.height - panelRect.height - VIEWPORT_MARGIN);

  return {
    left: Math.min(maxLeft, Math.max(minLeft, left)),
    top: Math.min(maxTop, Math.max(minTop, top)),
  };
}

function isFormControl(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && Boolean(target.closest('button, input, select, textarea, label, a, [role="button"], [contenteditable="true"]'));
}

export function useDraggableDialog(open: boolean, options: DraggableDialogOptions = {}) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<DialogPosition | null>(null);
  const initialPlacement = options.initialPlacement ?? 'center';

  useEffect(() => {
    if (!open) setPosition(null);
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;

    const overlayRect = overlayRef.current?.getBoundingClientRect();
    const panelRect = panelRef.current?.getBoundingClientRect();
    if (!overlayRect || !panelRect) return;

    const initialPosition = initialPlacement === 'right'
      ? {
          left: overlayRect.width - panelRect.width - VIEWPORT_MARGIN,
          top: RIGHT_PANEL_TOP,
        }
      : {
          left: (overlayRect.width - panelRect.width) / 2,
          top: (overlayRect.height - panelRect.height) / 2,
        };

    setPosition(clampToBounds(
      initialPosition.left,
      initialPosition.top,
      panelRect,
      overlayRect,
    ));
  }, [initialPlacement, open]);

  useEffect(() => {
    if (!position) return;

    const handleResize = () => {
      const overlayRect = overlayRef.current?.getBoundingClientRect();
      const panelRect = panelRef.current?.getBoundingClientRect();
      if (!overlayRect || !panelRect) return;
      setPosition(current => current ? clampToBounds(current.left, current.top, panelRect, overlayRect) : current);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [position]);

  const onPointerDown = (event: PointerEvent<HTMLElement>) => {
    if (event.button !== 0 || isFormControl(event.target)) return;

    const rect = panelRef.current?.getBoundingClientRect();
    const overlayRect = overlayRef.current?.getBoundingClientRect();
    if (!rect || !overlayRect) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;

    const handlePointerMove = (moveEvent: globalThis.PointerEvent) => {
      const nextLeft = moveEvent.clientX - overlayRect.left - offsetX;
      const nextTop = moveEvent.clientY - overlayRect.top - offsetY;
      setPosition(clampToBounds(nextLeft, nextTop, rect, overlayRect));
    };

    const handlePointerUp = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };

    setPosition(clampToBounds(rect.left - overlayRect.left, rect.top - overlayRect.top, rect, overlayRect));
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp, { once: true });
  };

  const panelStyle: CSSProperties = position
    ? { left: position.left, top: position.top }
    : { left: 0, top: 0, visibility: 'hidden' };

  return {
    overlayRef,
    panelRef,
    panelStyle,
    dragHandleProps: {
      onPointerDown,
    },
  };
}
